/**
 * Demo-only styles. This file is not part of the published package (the
 * `files` field ships `dist` and `src` only), so nothing here reaches
 * consumers of the library.
 */

/**
 * The "modifier only" demo writes its own CSS, exactly as a consumer would: a
 * wrapping flex row, and cells with a base height (from CSS, never inline,
 * since the modifier clears inline sizes each pass). The cells are content
 * cards rather than images, to show the modifier is content-agnostic.
 */
.bento {
  /* Establish a query container so the cell shapes below respond to this
     element's width (driven by the demo's slider), not the viewport. */
  container-type: inline-size;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  /* A faint fill and padding so the container's own bounds are visible as the
     width slider changes it. The modifier subtracts this padding from the
     width it lays items out into. */
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: color-mix(in srgb, currentColor 8%, transparent);
}

.bento__cell {
  position: relative;
  height: 120px;
  overflow: hidden;
  border-radius: 0.75rem;
  color: white;
}

/* Aspect ratios live in classes, so the container query can reshape them. */
.bento__cell--wide {
  aspect-ratio: 3 / 1;
}
.bento__cell--banner {
  aspect-ratio: 2 / 1;
}
.bento__cell--landscape {
  aspect-ratio: 3 / 2;
}
.bento__cell--standard {
  aspect-ratio: 4 / 3;
}
.bento__cell--square {
  aspect-ratio: 1 / 1;
}
.bento__cell--portrait {
  aspect-ratio: 2 / 3;
}

/* As the container narrows, flatten the extreme shapes toward square so cards
   don't become slivers. This reacts to the width slider, not the window. */
@container (max-width: 520px) {
  .bento__cell--wide {
    aspect-ratio: 1 / 1;
  }
  .bento__cell--banner {
    aspect-ratio: 1 / 1;
  }
  .bento__cell--landscape {
    aspect-ratio: 1 / 1;
  }
  .bento__cell--portrait {
    aspect-ratio: 1 / 1;
  }
  .bento__cell--standard {
    aspect-ratio: 1 / 1;
  }
}

.bento__meta {
  position: absolute;
  left: 0.85rem;
  bottom: 0.7rem;
  display: flex;
  flex-direction: column;
  text-shadow: 0 1px 3px rgb(0 0 0 / 30%);
}

.bento__value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.1;
}

.bento__label {
  font-size: 0.8rem;
  opacity: 0.9;
}

:root {
  color-scheme: light dark;
  font-family:
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    sans-serif;
}

body {
  margin: 0;
  line-height: 1.5;
}

.demo {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 5rem 4rem;
}

.demo header {
  margin-bottom: 2rem;
}

.demo h1 {
  margin: 0 0 0.5rem;
}

.demo h2 {
  margin: 0 0 0.25rem;
}

.demo section {
  margin-top: 2.5rem;
}

.demo section > p {
  margin: 0 0 1rem;
  color: color-mix(in srgb, currentColor 65%, transparent);
}

.demo code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.9em;
  padding: 0.1em 0.35em;
  border-radius: 0.25rem;
  background: color-mix(in srgb, currentColor 12%, transparent);
}

.demo figure {
  margin: 0;
  width: 100%;
  height: 100%;
  position: relative;
}

.demo figcaption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  color: white;
  background: linear-gradient(transparent, rgb(0 0 0 / 65%));
}

.balance-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;
}

/* A control panel shared by the interactive demos (playground, bento). */
.demo-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.5rem;
  margin: 0 0 1rem;
  padding: 1rem;
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  border-radius: 0.5rem;
}

.demo-form .control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.demo-form .control-label {
  font-weight: 600;
}

.demo-form .control-value {
  min-width: 2.5rem;
  font-variant-numeric: tabular-nums;
  color: color-mix(in srgb, currentColor 65%, transparent);
}

.playground-snippet {
  margin: 0 0 1.5rem;
}

.playground-stage {
  /* Width is set inline by the playground so the grid re-balances as it
     changes; margin-inline keeps a narrowed stage from drifting. */
  margin-inline: auto;
}

.demo-controls {
  margin: 0 0 1rem;
}

.demo-controls button {
  font: inherit;
  padding: 0.35rem 0.8rem;
  border: 1px solid color-mix(in srgb, currentColor 35%, transparent);
  border-radius: 0.4rem;
  background: color-mix(in srgb, currentColor 8%, transparent);
  color: inherit;
  cursor: pointer;
}

.demo-controls button:hover {
  background: color-mix(in srgb, currentColor 16%, transparent);
}
